import { HttpClient } from "@angular/common/http"
import { Injectable } from "@angular/core"
import { PriceInputMode, VatExemptionType } from "fakturnia-shared"
import { API_HOST } from "environments/environment"

@Injectable({
    providedIn: 'root'
})
export class SettingsApiService {

    constructor(private http: HttpClient) { }

    public setScheme(value: 'light' | 'dark' | 'auto') {
        return this.http.patch(API_HOST + 'settings/scheme', { scheme: value})
    }
    
    public setDefaultTaxRate(data) {
        return this.http.patch(API_HOST + 'settings/default-tax-rate', { defaultTaxRate : data})
    }

    public updateDefaultVisibleSellerFields(data) {
        return this.http.patch(API_HOST + 'settings/default-visible-seller-fields', { defaultVisibleSellerFields : data})
    }

    public updateNumberingType(numberingType) {
        return this.http.patch(API_HOST + 'settings/numbering-type', { numberingType : numberingType})
    }

    public updateNumberingFormat(numberingFormat) {
        return this.http.patch(API_HOST + 'settings/numbering-format', { numberingFormat : numberingFormat})
    }

    public updateDocumentSymbols(documentType, symbol) {
        return this.http.patch(API_HOST + 'settings/document-symbols', { documentType: documentType, symbol: symbol })
    }

    public updateEnableGTU(enableGTU: boolean) {
        return this.http.patch(API_HOST + 'settings/gtu', { enableGTU: enableGTU })
    }

    public updateEnablePKWiU(enablePKWiU: boolean) {
        return this.http.patch(API_HOST + 'settings/pkwiu', { enablePKWiU: enablePKWiU })
    }

    public updateShowPKWiU(showPKWiU: boolean) {
        return this.http.patch(API_HOST + 'settings/show-pkwiu', { showPKWiU: showPKWiU })
    }

    /**
     * Aktualizuje ustawienia podstawy zwolnienia z VAT (nowy model KSeF)
     */
    public updateVatExemptionSettings(
        defaultVatExemptionType: VatExemptionType | null,
        defaultVatExemptionLegalBasis: string | null,
        showVatExemptionBase: boolean,
        defaultVatExemptionEnabled?: boolean
    ) {
        return this.http.patch(API_HOST + 'settings/vat-exemption-base', {
            defaultVatExemptionType,
            defaultVatExemptionLegalBasis,
            showVatExemptionBase,
            defaultVatExemptionEnabled
        })
    }

    /**
     * @deprecated Użyj updateVatExemptionSettings() dla nowego modelu KSeF
     */
    public updateVatExemptionBase(defaultVatExemptionBase: string | null, defaultVatExemptionBaseCustom: boolean, showVatExemptionBase?: boolean) {
        return this.http.patch(API_HOST + 'settings/vat-exemption-base', { 
            defaultVatExemptionBase, 
            defaultVatExemptionBaseCustom,
            showVatExemptionBase
        })
    }

    /**
     * Aktualizuje ustawienie wyświetlania QR kodu weryfikacji KSeF na fakturach
     */
    public updateShowKsefQrCode(showKsefQrCode: boolean) {
        return this.http.patch(API_HOST + 'settings/ksef-qr', { showKsefQrCode: showKsefQrCode })
    }

    /**
     * Aktualizuje ustawienie wyświetlania przeliczonej wartości brutto w PLN
     */
    public updateShowConvertedAmount(showConvertedAmount: boolean) {
        return this.http.patch(API_HOST + 'settings/show-converted-amount', { showConvertedAmount })
    }

    /**
     * Aktualizuje ustawienie włączenia/wyłączenia funkcji KSeF w aplikacji
     */
    public updateEnableKsefFeatures(enableKsefFeatures: boolean) {
        return this.http.patch(API_HOST + 'settings/ksef-features', { enableKsefFeatures })
    }

    public updateEnableJst(enableJst: boolean) {
        return this.http.patch(API_HOST + 'settings/jst', { enableJst })
    }

    public updateDateFormat(dateFormat: string) {
        return this.http.patch(API_HOST + 'settings/date-format', { dateFormat })
    }

    public updatePriceInputMode(priceInputMode: PriceInputMode) {
        return this.http.patch(API_HOST + 'settings/price-input-mode', { priceInputMode })
    }

    /**
     * Aktualizuje domyślny status zapłaty dla nowo wystawianych dokumentów (0 = Zapłacono, 1 = Do zapłaty)
     */
    public updateDefaultPaymentStatus(defaultPaymentStatus: number) {
        return this.http.patch(API_HOST + 'settings/default-payment-status', { defaultPaymentStatus })
    }

    /**
     * Aktualizuje ustawienia automatycznych adnotacji dla nowych dokumentów
     * (master + domyślna procedura marży + domyślny MPP). Partial update.
     */
    public updateAutoAnnotationsSettings(payload: {
        autoAnnotations?: boolean
        defaultMarginProcedureEnabled?: boolean
        defaultMarginProcedureId?: number | null
        defaultSplitPayment?: boolean
    }) {
        return this.http.patch(API_HOST + 'settings/auto-annotations', payload)
    }

    // ─── Powiadomienia push ─────────────────────────────────────

    public updateBrowserPush(browserPushEnabled: boolean) {
        return this.http.patch(API_HOST + 'settings/browser-push', { browserPushEnabled })
    }

    public updateMobilePush(mobilePushEnabled: boolean) {
        return this.http.patch(API_HOST + 'settings/mobile-push', { mobilePushEnabled })
    }

    public getVapidPublicKey() {
        return this.http.get<any>(API_HOST + 'settings/push/vapid-public-key')
    }

    public pushSubscribe(subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) {
        return this.http.post(API_HOST + 'settings/push/subscribe', subscription)
    }

    public pushUnsubscribe(endpoint: string) {
        return this.http.post(API_HOST + 'settings/push/unsubscribe', { endpoint })
    }

    /**
     * Oznacza onboarding jako wyświetlony
     */
    public markOnboardingSeen() {
        return this.http.patch(API_HOST + 'settings/onboarding-seen', {})
    }

    /**
     * Zapisuje feedback z przewodnika po systemie
     */
    public saveTourFeedback(data: { rating: number | null; completed: boolean; stepsCompleted: number; totalSteps: number; device: string; durationSeconds: number | null }) {
        return this.http.post(API_HOST + 'settings/tour-feedback', data)
    }
   
}